import type {
  IdCheckSelectionEnum,
  R2RSelectionEnum,
} from '@/components/VerifyId/Sidebar/IDVTVerify'
import type { Outcome, ReferenceOutcomeStatus } from '@/lib/apollo/model/outcome'
import type { ReferenceTier } from '@/lib/apollo/query/get-applicant'
import type { SectionName } from '@/lib/apollo/query/get-document-requests'

import type { Currency, IncomeProperties } from './Income'
import type { AddressLandlordReference, LandlordReference } from './Landlord'

export interface ItemAndValue {
  key: string
  value: any
}

export interface EntityData {
  items?: ItemAndValue[]
  checkListItems?: ItemAndValue[]
  ccj?: CCJ[]
  documentProof?: string
}

export interface IItemAndStatus {
  tag: string
  status: string
}

export interface Person {
  name: Name
}

export interface Name {
  firstname: string
  lastname: string
}
export interface Activity {
  customerClientName?: string
  subjectId?: string
  applicationId?: string
  customerId?: string
  avatar?: string
  title?: string
  type?: string
  desc?: string
  message?: string
  initiatedBy?: string
  createdBy?: string
  assets?: Asset[]
  results?: Result[]
  additionalSubjects?: AdditionalSubject[]
  attachedSubjects?: AdditionalSubject[]
  timestamp?: string
  logicAppRunId?: string
  context?: Context
  recommendationId?: string
}

export interface Result {
  name?: string
  outcome?: number
  properties?: ResultProperty[]
}

export interface ResultProperty {
  name?: string
  supportingData?: string
  dataMatches?: number
  decisionInfo?: string[]
  result?: number
}

export interface Task {
  id?: string
  title: string
  description: string
  priority?: number
  completed?: boolean
  customerId?: string
  applicationId?: string
  subjectId?: string
  completedAt?: string
  dueBy?: string
}

export enum OpenBankingProvider {
  creditKudos = 'credit_kudos',
  tink = 'tink',
  default = 'default',
}

export enum IdentityProvider {
  Default = 'Default',
  Credas = 'Credas',
  Entrust = 'Entrust',
}

export enum IDVT {
  NotRequired = 'NotRequired',
  UKOnly = 'UKOnly',
  Required = 'Required',
}

export enum ChaseType {
  Default = 'default',
  AdvancedPlus = 'plus',
  Priority = 'priority',
}

interface JourneyConfig {
  buildReportFromOutcome?: boolean
  referenceGroup?: string
  openBankingProvider?: OpenBankingProvider
  identityProvider?: IdentityProvider
  idvt?: IDVT
  chaseType?: ChaseType
  swimLaneGroup?: string
  mandatedTrustedSourcesEnabled?: boolean
}

export enum JourneyType {
  essential = 'essential',
  employer = 'employer',
  advanced = 'goodlord_advanced',
  advanced_unmapped = 'goodlord-advanced',
  corporate = 'corporate',
  dataCollection = 'data-collection',
}

export enum IDVTIDCheckStatus {
  Pass = 'Pass',
  Fail = 'Fail',
  Complete = 'Complete',
  ActionRequired = 'ActionRequired',
  InProgress = 'InProgress',
  NotPerformed = 'NotPerformed',
}

export interface Milestone {
  event: string
  createdAt: string
}

export interface Application {
  customerClientName?: string
  studentIDContext?: string
  IDVTIDCheckStatus?: IDVTIDCheckStatus
  hasPendingIDVTChecks?: boolean
  IDVTR2RStatus?: IDVTIDCheckStatus
  reference?: EmployerReference | LandlordReference | AccountantReference
  type: string
  id?: string
  subjectId: string
  applicationId: string
  customerId: string
  name?: string
  firstName?: string
  avatar?: string
  outcome?: Outcome
  lastName?: string
  dob?: string
  assets?: Asset[]
  subjectAssets?: SubjectAsset[]
  subjects?: Subject[]
  context?: Context
  identityData?: EntityData
  additionalQuestions?: AdditionalQuestions
  advisories?: Advisory[]
  employmentData?: EntityData
  attachedSubjects?: AdditionalSubject[]
  additionalTenants?: AdditionalSubject[]
  results?: Result[]
  journeyConfig?: JourneyConfig
  timestamp: string
  milestones: Milestone[]
  agent: Agent
  referenceTier: ReferenceTier
}

export type EmployerRelationshipTypes = 'HRDepartment' | 'LineManager' | 'Unknown'
export type ContractExtensionType = 'Unknown' | 'HasExtension' | 'NoExtension'

export interface Agent {
  name: string
  phone: string
}

interface SubjectAsset {
  type: string
  attributes: {
    priceShare: number
    affordabilityRatio: number
  }
}

export interface Subject {
  id?: string
  context?: Context
}

export interface Highlight {
  key?: string
  value?: string
  status?: string
  location?: any
  details?: any
}

export interface Asset {
  attributes: AssetAttribute
  price: string
}

interface AssetAttribute {
  address: Address
  term: number
  price: number
  startDate: string
}

interface AdditionalQuestion {
  promptId?: string
  answer?: string
}

export interface AdditionalQuestions {
  items: AdditionalQuestion[]
  ccj?: CCJ[]
  documentProof?: string
}

export interface CCJ {
  caseNumber?: string
  court?: string
  courtCode?: string
  courtDate?: string
  satisfiedDate?: string
  amount?: number
  currency?: string
  status?: number
  type?: string
  name?: string
  meetsRgCriteria?: boolean
}

export interface AdditionalSubject {
  name: string
  avatar?: string
  customerId: string
  applicationId: string
  subjectId: string
  email: string
  type: string
  outcome?: Outcome
  attachedSubjects?: AdditionalSubject[]
  context?: Context
  journeyConfig?: JourneyConfig
  referenceTier: ReferenceTier
}

export interface ReferenceOutcome {
  status: ReferenceOutcomeStatus
  comments?: string[]
}

interface Advisory {
  category: string
  description: string
  isActive: boolean
  name: string
  severity: number
}

interface Country {
  name?: string
  nationality?: string
}

export interface Identity {
  idNumber?: string
  idFirstName?: string
  idLastName?: string
  idDob?: string
  nationality?: string
  idType?: string
  idEvidence?: string[]
  r2RShareCode: string
  selfieEvidence?: string
  facialMatch?: boolean
  facialMatchConfidence?: number
  expiryDate?: string
  issueDate?: string
  nationalInsuranceNumber?: string
  rightToRentAdditionalContext?: string
  applicantProvidedRightToRentCode?: boolean
  verification?: IdentityVerification
}

export interface IdentityVerification {
  idCheckStatus?: IdCheckSelectionEnum
  r2RStatus?: R2RSelectionEnum
}

export interface Address {
  isRegisteredOwner?: boolean
  normalizedLine1?: string
  id?: string
  bedroomIdentifier?: string
  name?: string
  nameOrNumber?: string
  line1?: string
  status?: string
  line2?: string
  city?: string
  county?: string
  country?: string
  postcode?: string
  latitude?: number
  longitude?: number
  identifiers?: string[]
  evidence?: string[]
  addressType: string
  equifaxId?: string
  udprn?: string
  durationAtAddress?: string
  startDate?: string
  electoralRollConfirmation?: ElectoralRollConfirmation
  reference?: AddressLandlordReference
  verification?: AddressVerification
  paymentDetails?: PaymentDetails | null
}

interface PaymentDetails {
  isFromBankAccount: boolean
  recipient?: string
  amount?: number
}

interface AddressVerification {
  state: string
}

interface ElectoralRollConfirmation {
  status?: number
  yearsConfirmed?: boolean
}

export interface Financial {
  maximumAffordabiity?: number
  affordabilityThreshold?: number
  totalIncome?: number
  totalOutgoing?: number
}

export interface OpenBankingConnectionFailure {
  timestamp: string
  reason: string
}

export interface OpenBankingConnection {
  source: string
  status: string
  completedAt?: string | null
  optOutReason?: IntegrationOptOutReason | null
  outcome?: OpenBankingOutcome | null
  connectionFailures?: OpenBankingConnectionFailure[] | null
  reportUrl?: string | null
}

export type OptOutReasonCategory =
  | 'bankNotListed'
  | 'connectionFailed'
  | 'incomeAccuracy'
  | 'privacyConcerns'
  | 'somethingElse'
  | 'userOptedOut'
  | 'overlayDismissed'
  | 'declinedTinkJourney'
  | 'unableToCompleteTinkJourney'

export interface OpenBankingOutcome {
  estimatedNetIncome: number
  estimatedGrossIncome?: number
  estimatedAnnualNetIncome?: number
  estimatedAnnualGrossIncome?: number
  balance?: number
  comments: string[]
  incomeId?: string
}

export interface Employment {
  status?: string
  company?: string
  position?: string
  referee?: string
}

export interface Document {
  fileName: string
  thumbnail?: string
  watermarkedFileName?: string
  watermarkedThumbnail?: string
  specifiedType?: string
  isItemAPdf?: boolean
  id?: string
  createdAt?: string
  updatedAt?: string
  uploadedBy?: DocumentAuthor
  properties?: DocumentProperties
  requestDetails?: DocumentRequest
}

export interface DocumentRequest {
  additionalContext?: string
  documentSection?: SectionName
}

export interface DocumentProperties {
  authorName?: string
  createdDate?: string
  modifiedDate?: string
  documentTitle?: string
  softwareUsedToCreate?: string
}

export type AuthorType = 'Applicant' | 'ReferencingTeam' | 'Agent' | 'System'

export interface DocumentAuthor {
  id: string
  type: AuthorType
  authorName?: string
  authorEmail?: string
}

export enum FraudStatus {
  notFraudulent = 'NotFlagged',
  awaitingFraudTeamReview = 'AwaitingFraudTeamReview',
  potential = 'FlaggedAsPotentiallyFraudulent',
  confirmed = 'ConfirmedFraudulent',
}

export enum FraudDecision {
  confirmedFraud = 'ConfirmedFraud',
  suspectedFraud = 'SuspectedFraud',
  internalRiskHighRisk = 'InternalRiskHighRisk',
  internalRiskClaimHighRisk = 'InternalRiskClaimHighRisk',
  internalRiskMediumRisk = 'InternalRiskMediumRisk',
}

export interface CompanyDetails {
  name: string
  companyNumber?: string
  incorporationDate?: string
  address: CompanyAddress
  verification?: CompanyVerification
  credit?: CompanyCredit
  finance?: CompanyFinance
}

export interface CompanyFinance {
  assets: number
  liabilities: number
  unrestrictedFunds: number
  cashAndEquivalent: number
  netAssets: number
  currentRatio: number
  verifiedBy?: VerifiedBy
}

export interface CompanyCredit {
  creditScore: number
  creditLimit: number
  ccJs: CompanyCCJ[]
  verifiedBy?: VerifiedBy
}

export interface CompanyCCJ {
  amount: number
  state: CompanyCCJState
  courtDate: string
}

export type CompanyCCJState = 'Active' | 'Satisfied'

export interface CompanyAddress {
  name: string
  line1: string
  line2: string
  city: string
  country: string
  postcode: string
}

export interface CompanyVerification {
  companyHouseCheck: CompanyHouseCheck
  companyType: CompanyType
  incorporationDate: string
  organisationStatus: OrganisationStatus
  filedAccount?: string
  companyNumber: string
  verifiedBy?: VerifiedBy
}

export interface VerifiedBy {
  name: string
  email: string
}

export type CompanyHouseCheck = 'Verified' | 'NotVerified' | 'Overseas'

export type CompanyType =
  | 'PrivateLimitedCompany'
  | 'PrivateLimitedCompanyByGuaranteeWithoutShareCapital'
  | 'PrivateLimitedCompanyByGuaranteeWithoutShareCapitalUseOfLimitedExemption'
  | 'PublicLimitedCompany'
  | 'LimitedLiabilityPartnership'
  | 'OverseasCompanyRequiredToPublishAccountsUnderParentLaw'
  | 'CharitableIncorporatedOrganisation'
  | 'CooperativeAndCommunityBenefitSocietyWithCharitableStatus'

export type OrganisationStatus =
  | 'Active'
  | 'Dormant'
  | 'Dissolved'
  | 'Liquidation'
  | 'Insolvent'
  | 'Administration'

export interface EmergencyContact {
  name: string | null
  relationship: string | null
  email: string | null
  phone: string | null
}

export interface Context {
  firstName?: string
  lastName?: string
  middleName?: string
  email?: string
  mobile?: string
  dob?: string
  jobRole?: string
  nationality?: string
  country?: Country
  identity?: Identity
  addresses?: Address[]
  financial?: Financial
  documents?: Document[]
  results?: Result[]
  employment?: Employment[]
  incomes?: Income[]
  journeyType?: JourneyType
  fraud?: Fraud
  name?: string
  company?: CompanyDetails
  credit?: CreditData
  emergencyContactDetails?: EmergencyContact
}

export interface CreditData {
  provider: string
  identityConfirmed: boolean
  residencyConfirmed: boolean
  declaredCredit: DeclaredCredit[]
  adverseCredit: AdverseCredit[]
}

export interface DeclaredCredit {
  type: string
  amount: number
  address: string | undefined
}

export interface AdverseCredit {
  type: AdverseCreditType
  amount: number
  address: string | undefined
  status: AdverseCreditStatus
  caseNumber: string | undefined
  courtDate: string
}

type AdverseCreditStatus = 'Satisfied' | 'Unsatisfied' | 'Unknown'

type AdverseCreditType = 'CCJ' | 'Bankruptcy' | 'Other'

export interface Fraud {
  status: FraudStatus
  reason: string
  decision?: FraudDecision
}

export interface GuaranteedBonus {
  value: number
  currency: Currency
  frequency: string
  averageHoursAWeek?: any
}

export type IncomeType =
  | 'Benefits'
  | 'Investments'
  | 'investments'
  | 'Student'
  | 'Pension'
  | 'Savings'
  | 'employment'
  | 'Unemployment'
  | 'savings'
  | 'investment'
  | 'student'
  | 'pension'
  | 'benefits'
  | 'unemployment'

export interface Income {
  id: string
  incomeProperties: IncomeProperties
  supportingDocuments: SupportingDocument[]
  type: IncomeType
  status: string
  company: string
  position: string
  contractType: string
  contractSchedule: string
  startDate: string
  endDate: string
  address: Address
  evidenceType: string
  guaranteedBonus: GuaranteedBonus
  employerReference?: IncomeEmployerReference
  accountantReference?: IncomeAccountantReference
  hasAddedAccountantDetails?: boolean
  validation?: IncomeValidation
  provider: string
  idNumber: string
  institution: string
  taxReturnYearPeriod?: string
  directorPayType?: string
  konfirIntegration?: KonfirIntegration | null
  openBankingConnections?: OpenBankingConnection[] | null
  documentBypassReason?: DocumentBypassReason | null
  trustedSourcesRequired?: boolean
}

export interface DocumentBypassReason {
  category: string
  additionalInformation?: string | null
}

export interface KonfirIntegration {
  status: string
  outcome?: KonfirOutcome
  optOutReason?: IntegrationOptOutReason
}

export interface KonfirOutcome {
  status: ReferenceOutcomeStatus
  reasons: string[]
}

export interface IntegrationOptOutReason {
  category: string
  additionalInformation?: string | null
}

interface IncomeValidation {
  validatorId?: string
  validationSources?: number[]
  state: IncomeValidationState
  validatorPerson?: ValidatorPerson
  confirmed?: ConfirmedIncome
  unableToVerifyReason?: string
}

interface ConfirmedIncome {
  income: IncomeProperties
  guaranteedBonus?: IncomeProperties
}

interface ValidatorPerson {
  name: string
  email: string
}

export enum IncomeValidationState {
  PendingValidation = 0,
  ValidatedByReferee = 1,
  ValidatedByOpenBanking = 2,
  ValidatedByOffice = 3,
  FailedValidation = 4,
  ValidatedBySystem = 5,
  UnableToVerify = 6,
  ValidatedByPayrollProvider = 7,
  ValidatedByHMRC = 8,
}

export const validationToHeaderText = (validation: IncomeValidation | undefined) => {
  if (!validation) {
    return 'Pending validation'
  }

  switch (validation.state) {
    case IncomeValidationState.PendingValidation:
    case IncomeValidationState.FailedValidation:
      return 'Pending validation'
    case IncomeValidationState.UnableToVerify:
      return 'Unable to verify'
    default:
      return 'Validated'
  }
}

export const validationStateToMessage = (state: IncomeValidationState) => {
  switch (state) {
    case IncomeValidationState.PendingValidation:
      return 'Validation is pending completion'
    case IncomeValidationState.ValidatedByReferee:
      return 'Validated by referee'
    case IncomeValidationState.ValidatedByOffice:
      return 'Validated by office'
    case IncomeValidationState.ValidatedByOpenBanking:
      return 'Validated by open banking'
    case IncomeValidationState.FailedValidation:
      return 'Unable to automatically validate the information supplied'
    case IncomeValidationState.ValidatedBySystem:
      return 'Validated automatically'
    case IncomeValidationState.ValidatedByPayrollProvider:
      return 'Validated by payroll provider'
    case IncomeValidationState.ValidatedByHMRC:
      return 'Validated by HMRC'
  }
}

export interface IncomeAccountantReference {
  outcome: ReferenceOutcome
  subjectId: string
  name: string
  mobile: string
  email: string
  jobRole?: string
  income?: IncomeProperties
  incomeSource?: string
  startDate?: string
  taxReturnYearPeriod?: string
  incomeConfirmationType?: string
  additionalContext?: string
}

export interface AccountantReference {
  incomeSource: string
  startDate: string
  additionalContext: string
  outcome: ReferenceOutcome
  taxReturnYearPeriod: string
  incomeConfirmationType: string
  income: IncomeProperties
  supportingDocuments: SupportingDocument[]
}

export interface IncomeEmployerReference {
  subjectId: string
  name: string
  firstName: string
  lastName: string
  phone: string
  email: string
  jobRole?: string
  supportingDocuments: SupportingDocument[]
  referenceEmploymentId: string
  referenceMethod: string
  income?: IncomeProperties
  guaranteedBonus?: IncomeProperties
  jobTitle?: string
  refereeRelationship?: EmployerRelationshipTypes
  contractExtension?: ContractExtensionType
  contractType?: string
  contractFrequency?: string
  startDate?: string
  endDate?: string
  additionalContext?: string
  outcome: ReferenceOutcome
}

export interface EmployerReference {
  supportingDocuments: SupportingDocument[]
  referenceEmploymentId: string
  referenceMethod: string
  income: IncomeProperties
  guaranteedBonus?: IncomeProperties
  jobTitle?: string
  refereeRelationship?: EmployerRelationshipTypes
  contractExtension?: ContractExtensionType
  contractType?: string
  contractFrequency?: string
  startDate?: string
  endDate?: string
  additionalContext?: string
  outcome: ReferenceOutcome
}

export interface SupportingDocument {
  documentReference: string
  thumbnail: string
  type: string
  isValid?: boolean
}
